import type { Copy } from '../types/copy';

export const copyEs: Copy = {
  seo: {
    title: 'Alma Yoga | Yoga privado de lujo en Nueva York',
    description:
      'Yoga privado y personalizado en Nueva York. Sesiones a domicilio, online y en hoteles con instructora certificada. Recuperación, movilidad y espiritualidad. Bilingüe.',
  },
  nav: {
    services: 'Servicios',
    philosophy: 'Filosofía',
    about: 'Acerca',
    faq: 'Preguntas',
    contact: 'Contacto',
    book: 'Reservar',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    skip: 'Saltar al contenido',
    language: 'Idioma',
    call: 'Llamar a Alma Yoga',
  },
  hero: {
    tagline: 'Yoga más allá del mat',
    h1: 'Yoga privado de lujo, diseñado a tu medida.',
    sub: 'En tu espacio. Para tus objetivos. Para tu vida.',
    description:
      'Alma Yoga ofrece sesiones individuales e íntimas, pensadas para tu cuerpo, tu energía y tus intenciones. A domicilio en Nueva York. Online, desde cualquier lugar del mundo.',
    cta: 'Agenda una sesión privada',
    secondary: 'Ver servicios',
    imageAlt:
      'Instructora de yoga privado en postura del guerrero en una terraza de Manhattan al atardecer, con el Empire State al fondo',
  },
  concept: {
    title: '¿Qué es Alma Yoga?',
    lead: 'Una práctica única y personalizada. Diseñada para una sola persona: tú.',
    paragraphs: [
      'Alma Yoga es una práctica de yoga privada y personalizada, diseñada en torno a tus necesidades, tu cuerpo, tu energía y tus intenciones.',
      'Cada sesión parte de donde estás hoy — física, mental y espiritualmente. La práctica se adapta a ti, nunca al revés.',
      'Con 10 años de práctica personal y una certificación de 200 horas de Yoga Alliance, creo sesiones que van más allá de las asanas: respiración, filosofía y cómo estos principios funcionan en tu vida real.',
      'Si te estás recuperando de un viaje, entrenando para una carrera, trabajando con una lesión o buscando algo más profundo, tu sesión se diseña exactamente para eso.',
    ],
    facts: [
      { value: '10', label: 'años de práctica' },
      { value: '2016', label: 'enseñando desde' },
      { value: 'EN / ES', label: 'sesiones en ambos idiomas' },
    ],
    imageAlt:
      'Mat, manta y bloque de yoga en un loft luminoso de Manhattan, listos para una sesión privada a domicilio',
  },
  experience: {
    title: 'Una experiencia de yoga privado más allá del mat',
    subtitle: 'Qué esperar',
    intro: 'Alrededor de 60 minutos. Ocho momentos, una práctica continua.',
    steps: [
      {
        title: 'Conexión',
        body: 'Empezamos con una conversación: tu cuerpo, tu energía, tus intenciones y cualquier aspecto físico que deba conocer.',
      },
      {
        title: 'Personalización',
        body: 'Diseño la sesión en tiempo real y ajusto cada elemento sobre la marcha.',
      },
      {
        title: 'Movimiento',
        body: 'Asanas pensadas para tu cuerpo: fuerza, flexibilidad, movilidad o recuperación.',
      },
      {
        title: 'Respiración y conciencia',
        body: 'Pranayama y transiciones conscientes para calmar el sistema nervioso y afinar la atención.',
      },
      {
        title: 'Filosofía',
        body: 'Principios del yoga aplicados a la vida real: respirar en la dificultad, encontrar estabilidad, aprender a soltar.',
      },
      {
        title: 'Meditación',
        body: 'Guiada o en silencio. Lo que necesites ese día.',
      },
      {
        title: 'Savasana',
        body: 'Una relajación final extendida, donde la práctica se asienta y descansas en profundidad.',
      },
      {
        title: 'Integración',
        body: 'Una práctica que te acompaña mucho después de dejar el mat.',
      },
    ],
    emphasis: 'Cada elemento tiene un propósito. Cada momento está diseñado para ti.',
    cta: 'Reserva tu primera sesión',
  },
  services: {
    title: 'Nuestros servicios',
    intro:
      'Una práctica única y personalizada. Individual, en pareja o con un pequeño grupo de amigos o familia.',
    learnMore: 'Ver más',
    book: 'Reservar esta sesión',
    close: 'Cerrar',
    includes: 'Qué incluye',
    idealForLabel: 'Ideal para',
    items: [
      {
        id: 'in-home',
        label: 'A domicilio',
        title: 'Sesiones privadas a domicilio',
        description:
          'Tu práctica, en la privacidad de tu casa. A tu horario, en tu espacio, con atención enfocada en ti.',
        features: [
          'Personalización uno a uno',
          'Mañanas, tardes y fines de semana',
          'Adaptada a tu espacio y tu energía',
          'Privacidad y comodidad total',
        ],
        idealFor:
          'Profesionales con agendas llenas, quienes valoran su privacidad y quienes practican mejor en su propio espacio.',
        alt: 'Instructora de yoga privado guiando a un cliente en un departamento de lujo en Nueva York',
      },
      {
        id: 'online',
        label: 'Online',
        title: 'Sesiones privadas online',
        description:
          'Alma Yoga dondequiera que estés. La misma personalización por videollamada, pensada para viajeros, profesionales ocupados y quienes practican fuera de Nueva York.',
        features: [
          'Personalización completa por video',
          'Disponible en todo el mundo',
          'La misma calidad, online',
          'Horarios flexibles entre husos horarios',
        ],
        idealFor:
          'Viajeros frecuentes, personas fuera de Nueva York y quien busque constancia esté donde esté.',
        alt: 'Sesión privada de yoga online en una laptop, en un ambiente tranquilo en casa',
      },
      {
        id: 'athletes',
        label: 'Atletas',
        title: 'Yoga de recuperación y rendimiento',
        description:
          'Una práctica que complementa tu entrenamiento. Movilidad, flexibilidad, recuperación y conciencia corporal, para corredores, boxeadores, levantadores y cualquiera que entrene en serio.',
        features: [
          'Trabajo de movilidad y flexibilidad',
          'Recuperación y regeneración',
          'Conciencia corporal y equilibrio',
          'Complemento para cualquier deporte',
        ],
        idealFor:
          'Corredores, boxeadores, atletas de fuerza y habituales del gimnasio que buscan movilidad, recuperación y prevenir lesiones.',
        alt: 'Atleta practicando yoga de recuperación y movilidad en un espacio de entrenamiento en Brooklyn',
      },
      {
        id: 'travelers',
        label: 'Viajeros',
        title: 'Sesiones en viaje y en hotel',
        description:
          'Mantén tu práctica mientras viajas. Recuperación de vuelos largos, alivio del jet lag y sesiones para volver a tu centro en tu suite o en cualquier punto de la ciudad.',
        features: [
          'Sesiones en hoteles de Nueva York',
          'Recuperación del jet lag y del viaje',
          'Horarios que se adaptan a tu itinerario',
          'Personalizada según cómo llegas',
        ],
        idealFor:
          'Huéspedes de hoteles en Nueva York, viajeros de negocios y cualquiera que se recupere de un vuelo largo.',
        alt: 'Sesión privada de yoga restaurativo en una suite de hotel de lujo con vista al skyline de Manhattan',
      },
      {
        id: 'astrology',
        label: 'Carta natal',
        title: 'Yoga inspirado en tu carta natal',
        description:
          '¿Y si tu práctica se diseñara en torno a tu carta natal? Usamos tu carta como herramienta de autoexploración, para encontrar equilibrio y cultivar cualidades concretas.',
        features: [
          'La carta natal como herramienta de exploración',
          'Práctica alineada con tu energía',
          'Autoconocimiento a través del yoga',
          'Simbólica, nunca predictiva',
        ],
        idealFor:
          'Personas con curiosidad por la autoexploración que quieren una práctica con una capa simbólica y reflexiva.',
        alt: 'Práctica de yoga inspirada en la astrología con una carta natal dibujada a mano',
      },
      {
        id: 'groups',
        label: 'Grupos',
        title: 'Sesiones en grupos íntimos',
        description:
          'Comparte la práctica con tu pareja, tu familia o tus amigos más cercanos. Diseñada según la energía del grupo.',
        features: [
          'Parejas y familias',
          'Grupos de amigos cercanos',
          'Personalizada para el conjunto',
          'Un entorno privado e íntimo',
        ],
        idealFor: 'Parejas, familias y amigos cercanos que quieren compartir una práctica privada.',
        alt: 'Sesión privada de yoga para un grupo pequeño de amigos en un loft luminoso del SoHo',
      },
    ],
    styles: {
      title: 'Estilos de yoga',
      items: ['Hatha', 'Vinyasa Flow', 'Yin', 'Restaurativo', 'Slow Flow', 'Yoga suave'],
      more: 'y más',
    },
  },
  instructor: {
    title: 'Tu instructora',
    name: 'Pia',
    role: 'Fundadora y propietaria',
    motto: 'Aventurera de lo invisible, guerrera del espíritu.',
    bio: 'Para mí, el yoga es una práctica para armonizar cuerpo, mente y emociones. Un espacio para reconectar, encontrar calma y alegría, y volver a casa, a nosotros mismos — honrando el cuerpo como nuestro único hogar.',
    closing: 'Cultivando presencia y paciencia.',
    credentialsTitle: 'Formación y trayectoria',
    credentials: [
      'Instructora de yoga certificada — 200 horas, Yoga Alliance',
      'Formada en Sampoorna Yoga, India',
      '10 años de práctica personal',
      'Enseñando desde 2016',
      'Licenciada en Relaciones Públicas',
    ],
    imageAlt: 'Pia, instructora certificada de yoga privado en Nueva York',
    cta: 'Reserva una sesión con Pia',
  },
  why: {
    title: '¿Por qué Alma Yoga?',
    items: [
      {
        title: 'Privado y personalizado',
        body: 'Diseñado por completo en torno a ti, todo personalizado según tus necesidades.',
      },
      {
        title: 'Flexible y cómodo',
        body: 'En tu casa, online o en tu hotel. Fines de semana y feriados incluidos.',
      },
      {
        title: 'Experta y certificada',
        body: '10 años de práctica dedicada y formación profesional.',
      },
      {
        title: 'Enfoque holístico',
        body: 'Más allá de las asanas: respiración, filosofía y su aplicación en tu vida.',
      },
      {
        title: 'Bilingüe',
        body: 'Sesiones en inglés o en español, en el idioma en que te sientas en casa.',
      },
      {
        title: 'Bienestar y recuperación',
        body: 'Para atletas, viajeros y cuerpos que necesitan cuidado.',
      },
      {
        title: 'Espiritual y moderno',
        body: 'Una práctica ancestral en el contexto contemporáneo de Nueva York.',
      },
      {
        title: 'A tu ritmo',
        body: 'Tu ritmo. Tu camino. Solo tú y la práctica.',
      },
    ],
  },
  philosophy: {
    title: 'Yoga más allá del mat',
    opening: 'La práctica empieza en el mat. El verdadero trabajo sucede en la vida.',
    learnLabel: 'A través del yoga aprendemos',
    lessons: [
      'A respirar cuando las cosas se ponen difíciles',
      'A observar sin reaccionar de inmediato',
      'A encontrar estabilidad en la incomodidad',
      'A soltar lo que ya no nos sirve',
      'A presentarnos con intención',
    ],
    closing:
      'Son prácticas, no destinos. Se aplican en el mat y en cada momento después de él. Ahí es donde vive el verdadero cambio.',
  },
  testimonials: {
    title: 'Lo que dicen nuestros clientes',
    items: [
      {
        quote:
          'El enfoque de Pia es completamente distinto a cualquier clase de yoga que haya tomado. No se trata de seguir a alguien. Se trata de que te encuentren donde estás.',
        name: 'Daniela R.',
        context: 'Arquitecta · Upper West Side',
      },
      {
        quote:
          'Como atleta, necesitaba una recuperación que entendiera de verdad lo que mi cuerpo necesitaba. Esta práctica cambió mi forma de entrenar.',
        name: 'Marcus T.',
        context: 'Maratonista y boxeador amateur',
      },
      {
        quote:
          'La personalización es notable. Escucha, se adapta y crea algo que se siente hecho solo para mí.',
        name: 'Sofía L.',
        context: 'Viajera frecuente · Madrid y Nueva York',
      },
      {
        quote:
          'Por primera vez, el yoga se sintió espiritual y práctico a la vez. Uso lo que aprendí todos los días.',
        name: 'James K.',
        context: 'Abogado · Tribeca',
      },
    ],
  },
  memberships: {
    title: 'Membresías Alma',
    intro:
      'Dos caminos personalizados. Una intención: ayudarte a reconectar contigo, honrar tus propios ritmos y cultivar una mayor conciencia, dentro y fuera del mat.',
    byline: 'Alma Yoga · NYC',
    getStarted: 'Comenzar',
    footer: 'Para conocer precios y más información, ponte en contacto.',
    contactCta: 'Contáctanos',
    tiers: [
      {
        id: 'essential',
        name: 'Alma Essential',
        term: 'Membresía mensual',
        description:
          'Un camino mensual personalizado para reconectar contigo a través del movimiento, el autoconocimiento y una vida consciente.',
        groups: [
          {
            title: 'Tu práctica personalizada',
            items: [
              { text: '4 sesiones privadas de yoga al mes' },
              { text: 'Una práctica de yoga personalizada, adaptada a tus necesidades y objetivos' },
              { text: 'Acompañamiento por mensaje el día después de cada sesión' },
              { text: 'Un check-in privado mensual de 15 minutos' },
            ],
          },
          {
            title: 'Tu kit de bienestar personalizado',
            items: [
              {
                label: 'Ritual de la mañana:',
                text: 'Una breve práctica matutina con una guía ilustrada de asanas para empezar el día con intención.',
              },
              {
                label: 'Sueño y equilibrio emocional:',
                text: 'Una guía escrita de asanas para favorecer la relajación, el equilibrio emocional y un cierre del día más consciente.',
              },
            ],
          },
        ],
      },
      {
        id: 'signature',
        name: 'Alma Signature',
        term: 'Membresía de 3 meses',
        description:
          'Un camino más profundo hacia tus ritmos personales, tu bienestar emocional y las prácticas que acompañan tu crecimiento, pensado cuidadosamente para ti durante tres meses.',
        groups: [
          {
            title: 'Tu práctica personalizada',
            items: [
              { text: '12 sesiones privadas de yoga en 3 meses' },
              { text: 'Una práctica de yoga personalizada, adaptada a tus necesidades y objetivos' },
              { text: 'Acompañamiento por mensaje el día después de cada sesión' },
              { text: 'Un check-in privado mensual de 15 minutos' },
            ],
          },
          {
            title: 'Tu kit de bienestar personalizado',
            items: [
              {
                label: 'Ansiedad y equilibrio emocional:',
                text: 'Una práctica guiada en audio, personalizada, para ayudarte a atravesar momentos de ansiedad y sobrecarga emocional.',
              },
              {
                label: 'Estrés y enraizamiento:',
                text: 'Una guía ilustrada de asanas para liberar tensión, reconectar con tu cuerpo y cultivar una sensación de arraigo.',
              },
              {
                label: 'Sueño y descanso:',
                text: 'Una guía ilustrada de asanas personalizada, diseñada para ayudarte a bajar el ritmo, relajarte y prepararte para descansar.',
              },
            ],
          },
          {
            title: 'Apoyo integral al bienestar',
            items: [
              {
                text: 'Acceso a un kinesiólogo cuando lo necesites, con apoyo adicional para el movimiento, la movilidad y el bienestar físico.',
              },
            ],
          },
        ],
      },
    ],
  },
  press: {
    featuredIn: 'Destacada en',
    byline: 'Por Pía Escobedo',
    headline: 'Presence Is a State of Happiness',
    sub: 'La filosofía de bienestar de Pía Escobedo, publicada en Beyond the Lobby.',
    excerpt:
      'La práctica del yoga ofrece un camino de regreso al presente a través de la respiración. Al fin y al cabo, nuestra única vida real existe en el momento presente: el pasado ya pasó y el futuro es solo imaginación.',
    cta: 'Leer el artículo completo',
    readMore: 'Leer más',
    close: 'Cerrar artículo',
    prev: 'Página anterior',
    next: 'Página siguiente',
    page: 'Página',
    of: 'de',
    pageAlts: [
      'Artículo de Beyond the Lobby "Presence Is a State of Happiness" de Pía Escobedo, primera página',
      'Prácticas diarias de bienestar de Pía Escobedo en Beyond the Lobby, página ilustrada',
      'Pía Escobedo, profesora argentina de yoga en Nueva York, practicando yoga en la playa',
      'Página de Beyond the Lobby sobre sesiones privadas de yoga en habitaciones de hotel con Pía Escobedo',
    ],
    cards: [
      {
        title: 'Rituales diarios de bienestar',
        body: 'Cinco pequeñas prácticas a las que Pía vuelve cada día, desde el primer vaso de agua hasta una lista de gratitud antes de dormir.',
        page: 1,
      },
      {
        title: 'Conoce a Pía Escobedo',
        body: 'Profesora argentina de yoga y meditación que lleva presencia y equilibrio a la vida acelerada de la ciudad.',
        page: 2,
      },
      {
        title: 'Yoga en tu habitación de hotel',
        body: 'Sesiones privadas en la habitación, en inglés y español: bienestar integrado a tu estadía en Nueva York.',
        page: 3,
      },
    ],
  },
  approach: {
    title: '¿Qué hace que una práctica sea personalizada?',
    intro: 'Tu práctica puede adaptarse a cinco aspectos, casi siempre a la vez.',
    items: [
      { title: 'Tu cuerpo', body: 'Nivel físico, flexibilidad, fuerza, lesiones o limitaciones.' },
      { title: 'Tu energía', body: 'Si necesitas enraizarte, activarte o equilibrarte.' },
      {
        title: 'Tus objetivos',
        body: 'Rendimiento, recuperación, exploración espiritual o alivio del estrés.',
      },
      {
        title: 'Tu momento de vida',
        body: 'Lo que estás atravesando ahora: transiciones, desafíos, crecimiento.',
      },
      {
        title: 'Tus intereses',
        body: 'Filosofía, respiración, meditación, movimiento o una combinación de todo.',
      },
    ],
    kinesiology: {
      title: '¿Tienes una lesión?',
      body: 'Si tienes alguna preocupación física concreta, podemos sumar una consulta con kinesiólogo para que cada sesión sea segura y respete cualquier condición existente.',
    },
  },
  faq: {
    title: '¿Preguntas?',
    intro: 'Lo que suelen preguntar antes de una primera sesión. Para cualquier otra cosa, escríbeme.',
    contactLink: 'Hacer una pregunta',
    items: [
      {
        q: '¿Y si soy principiante?',
        a: 'Alma Yoga está pensado para todos los niveles. Si eres nuevo en el yoga, construiremos una base adaptada a tu cuerpo y a tu ritmo, con paciencia, cuidado y espacio para crecer.',
      },
      {
        q: '¿Y si tengo lesiones o limitaciones?',
        a: 'Aquí es donde la personalización marca la diferencia. Trabajamos dentro de lo que tu cuerpo necesita y, si hace falta, consultamos con un kinesiólogo. Tu práctica se adapta a ti.',
      },
      {
        q: '¿Cuánto duran las sesiones?',
        a: 'Las sesiones duran alrededor de 60 minutos, y la duración puede adaptarse a tus necesidades y tu agenda.',
      },
      {
        q: '¿Puedo tomar sesiones si estoy de viaje?',
        a: 'Por supuesto. Las sesiones online funcionan desde cualquier lugar y, si estás en Nueva York, podemos organizar sesiones en tu hotel.',
      },
      {
        q: '¿Cómo funciona el yoga inspirado en la carta natal?',
        a: 'Usamos tu carta natal como herramienta simbólica de autoexploración. Ayuda a identificar cualidades a cultivar y áreas que buscan equilibrio, y tu práctica se diseña en torno a esas observaciones.',
      },
      {
        q: '¿Es espiritual o es fitness?',
        a: 'Ambas cosas, y ninguna de forma exclusiva. Combinamos movimiento, respiración, filosofía y atención plena. Vas a sentir tanto los beneficios físicos como la profundidad.',
      },
      {
        q: '¿Cómo reservo mi primera sesión?',
        a: 'Escríbeme a través del formulario o llámame directamente. Tendremos una breve conversación sobre tus necesidades, objetivos y preferencias para diseñar tu primera práctica.',
      },
      {
        q: '¿Puedo hacer sesiones con amigos o familia?',
        a: 'Sí. Ofrezco sesiones para grupos pequeños: parejas, familias o amigos cercanos. La misma personalización, en una experiencia compartida.',
      },
    ],
  },
  contact: {
    title: '¿Empezamos?',
    sub: 'Creemos una práctica diseñada para ti.',
    form: {
      name: 'Nombre completo',
      email: 'Email',
      phone: 'Teléfono',
      service: 'Servicio de interés',
      servicePlaceholder: 'Elige un servicio',
      other: 'Otro / aún no lo sé',
      schedule: 'Horario preferido',
      scheduleOptions: {
        morning: 'Mañana',
        afternoon: 'Tarde',
        evening: 'Noche',
        flexible: 'Flexible',
      },
      language: 'Idioma de la sesión',
      languageOptions: { en: 'Inglés', es: 'Español' },
      message: 'Mensaje',
      optional: 'Opcional',
      messagePlaceholder: 'Cuéntame sobre tu práctica, tus objetivos o necesidades',
      required: 'Obligatorio',
      submit: 'Enviar por WhatsApp',
      whatsappIntro: 'Hola Pia, me gustaría solicitar una sesión con Alma Yoga.',
      errors: {
        name: 'Por favor, escribe tu nombre.',
        email: 'Escribe un email válido.',
        phone: 'Escribe un teléfono en el que pueda contactarte.',
        service: 'Elige un servicio.',
      },
      successTitle: 'Casi listo: solo falta enviar.',
      successBody:
        'WhatsApp se abrió con tus datos listos. Envía el mensaje y te respondo en menos de 24 horas. Si no se abrió, escríbeme al +1 (917) 805-4352.',
      another: 'Enviar otra solicitud',
      privacyNote: 'Tus datos solo se usan para coordinar tu sesión.',
    },
    info: {
      phone: 'Teléfono',
      email: 'Email',
      whatsapp: 'WhatsApp',
      whatsappValue: 'Escríbeme en español o inglés',
      location: 'Dónde',
      locationValue: 'Nueva York — a domicilio, online y en hoteles',
      availability: 'Disponibilidad',
      availabilityValue: 'Flexible, incluidos fines de semana y feriados',
      response: 'Te respondo en menos de 24 horas.',
    },
  },
  footer: {
    tagline: 'Yoga más allá del mat',
    explore: 'Explorar',
    contact: 'Contacto',
    language: 'Idioma',
    follow: 'Síguenos',
    rights: 'Todos los derechos reservados.',
    privacy: 'Política de privacidad',
    terms: 'Términos',
    location: 'Nueva York',
  },
};
